import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Table,
  Badge,
  Modal,
} from "react-bootstrap";
import Select from "react-select";
import { Edit2, Trash2, Plus, Calendar, Download } from "lucide-react";
import Layout from "../../layout/Layout";
import { useAuth } from "../../context/AuthContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  equipmentPlanInsert,
  equipmentPlanUpdate,
  equipmentPlanDelete,
  equipmentPlanList,
  getPersonels,
} from "../../../api/bakim/equipmentReminder";

const monthsList = [
  { id: 1, name: "OCAK" },
  { id: 2, name: "ŞUBAT" },
  { id: 3, name: "MART" },
  { id: 4, name: "NİSAN" },
  { id: 5, name: "MAYIS" },
  { id: 6, name: "HAZİRAN" },
  { id: 7, name: "TEMMUZ" },
  { id: 8, name: "AĞUSTOS" },
  { id: 9, name: "EYLÜL" },
  { id: 10, name: "EKİM" },
  { id: 11, name: "KASIM" },
  { id: 12, name: "ARALIK" },
];

const trToEn = (text) => {
  if (!text) return "";
  return text
    .replace(/Ğ/g, "G")
    .replace(/ğ/g, "g")
    .replace(/Ü/g, "U")
    .replace(/ü/g, "u")
    .replace(/Ş/g, "S")
    .replace(/ş/g, "s")
    .replace(/İ/g, "I")
    .replace(/ı/g, "i")
    .replace(/Ö/g, "O")
    .replace(/ö/g, "o")
    .replace(/Ç/g, "C")
    .replace(/ç/g, "c");
};

function EquipmentPlanManagement() {
  const { user } = useAuth();
  const permissions = user?.permissions || [];

  const canInsert = permissions.includes("cleaningPlan_insert");
  const canUpdate = permissions.includes("cleaningPlan_update");
  const canDelete = permissions.includes("cleaningPlan_delete");

  const [plans, setPlans] = useState([]);
  const [personels, setPersonels] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  const initialFormState = {
    siraNo: "",
    department: "",
    machineName: "",
    operation: "",
    year: new Date().getFullYear(),
    repeatPerYear: "",
    responsibleUsers: [],
    months: [],
  };
  const [formData, setFormData] = useState(initialFormState);

  const initialStatusForm = {
    planId: null,
    monthId: null,
    status: "planned",
    description: "",
    doneDate: "",
  };
  const [statusForm, setStatusForm] = useState(initialStatusForm);

  useEffect(() => {
    fetchPersonels();
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await equipmentPlanList({ year: selectedYear });
      setPlans(res.data.data);
    } catch (err) {
      console.error("Plan yükleme hatası:", err);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const fetchPersonels = async () => {
    try {
      const res = await getPersonels();
      setPersonels(res.data.data.map((p) => ({ value: p._id, label: p.name })));
    } catch (err) {
      console.error("Personel yükleme hatası:", err);
    }
  };

  const handleOpenPlanModal = (plan = null) => {
    if (plan && !canUpdate) return;
    if (!plan && !canInsert) return;

    if (plan) {
      setEditingPlan(plan);
      setFormData({
        siraNo: plan.siraNo || "",
        department: plan.department || "",
        machineName: plan.machineName,
        operation: plan.operation,
        year: plan.year,
        repeatPerYear: plan.repeatPerYear || "",
        responsibleUsers: plan.responsibleUsers.map((u) =>
          typeof u === "object"
            ? { value: u._id, label: u.name }
            : { value: u, label: u }
        ),
        months: plan.months || [],
      });
    } else {
      setEditingPlan(null);
      setFormData({ ...initialFormState, year: selectedYear });
    }
    setShowPlanModal(true);
  };

  const handleSavePlan = async () => {
    try {
      const payload = {
        ...formData,
        siraNo:
          formData.siraNo !== "" ? parseInt(formData.siraNo, 10) : undefined,
        repeatPerYear: formData.repeatPerYear
          ? parseInt(formData.repeatPerYear, 10)
          : undefined,
        responsibleUsers: formData.responsibleUsers.map((u) => u.value),
      };

      if (editingPlan) {
        if (!canUpdate) return;
        await equipmentPlanUpdate({ _id: editingPlan._id, ...payload });
      } else {
        if (!canInsert) return;
        await equipmentPlanInsert(payload);
      }

      setShowPlanModal(false);
      fetchPlans();
    } catch (err) {
      alert("İşlem sırasında bir hata oluştu");
    }
  };

  const handleDelete = async (id) => {
    if (!canDelete) return;
    if (window.confirm("Bu planı silmek istediğinize emin misiniz?")) {
      try {
        await equipmentPlanDelete({ deletedId: id });
        fetchPlans();
      } catch (err) {
        alert("Silme işlemi başarısız");
      }
    }
  };

  const getMonthData = (plan, monthId) => {
    return plan?.months?.find((m) => m.month === monthId);
  };

  const handleMonthCellClick = async (plan, monthId) => {
    if (!canUpdate) return;

    const existingMonth = getMonthData(plan, monthId);

    if (!existingMonth) {
      const updatedMonths = [
        ...(plan.months || []),
        { month: monthId, status: "planned" },
      ];
      try {
        await equipmentPlanUpdate({ _id: plan._id, months: updatedMonths });
        fetchPlans();
      } catch (err) {
        console.error("Hızlı ekleme hatası");
      }
    } else {
      setStatusForm({
        planId: plan._id,
        monthId: monthId,
        status: existingMonth.status || "planned",
        doneDate: existingMonth.doneDate
          ? existingMonth.doneDate.split("T")[0]
          : "",
        description: existingMonth.note || existingMonth.description || "", // Açıklamayı state'e al
      });
      setShowStatusModal(true);
    }
  };

  const handleSaveStatus = async () => {
    if (!canUpdate) return;
    try {
      const plan = plans.find((p) => p._id === statusForm.planId);
      if (!plan) return;

      const updatedMonths = (plan.months || []).map((m) => {
        if (m.month === statusForm.monthId) {
          return {
            ...m,
            status: statusForm.status,
            doneDate: statusForm.status === "done" ? statusForm.doneDate : null,
            note: statusForm.description,
          };
        }
        return m;
      });

      await equipmentPlanUpdate({ _id: plan._id, months: updatedMonths });
      setShowStatusModal(false);
      fetchPlans();
    } catch (err) {
      alert("Durum güncelleme başarısız oldu.");
    }
  };

  const handleRemoveMonth = async () => {
    if (!canUpdate) return;
    try {
      const plan = plans.find((p) => p._id === statusForm.planId);
      const updatedMonths = (plan.months || []).filter(
        (m) => m.month !== statusForm.monthId
      );
      await equipmentPlanUpdate({ _id: plan._id, months: updatedMonths });
      setShowStatusModal(false);
      fetchPlans();
    } catch (err) {
      alert("Ay kaldırılamadı.");
    }
  };

  const handleExportPDF = () => {
    const doc = new jsPDF("landscape", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const tableColumn = [
      "NO",
      "DEPARTMAN",
      "EKIPMANLAR",
      "TEKRAR SAYISI",
      "SORUMLU",
      ...monthsList.map((m) => trToEn(m.name)),
    ];

    const tableRows = [];
    plans.forEach((plan, index) => {
      const cleanOperation = plan.operation
        ? trToEn(plan.operation.replace(/\s+/g, " ").trim())
        : "";

      const cleanMachineName = trToEn(plan.machineName);

      const machineInfo = cleanOperation
        ? `${cleanMachineName}\n(${cleanOperation})`
        : cleanMachineName;

      const repeat = plan.repeatPerYear ? `${plan.repeatPerYear}/YIL` : "-";

      const responsible = plan.responsibleUsers
        ?.map((u) => trToEn(u.name) || "Personel")
        .join(", ");

      const rowData = [
        plan.siraNo ? plan.siraNo.toString() : (index + 1).toString(),
        plan.department ? trToEn(plan.department) : "-",
        machineInfo,
        repeat,
        responsible,
      ];

      monthsList.forEach((m) => {
        const mData = getMonthData(plan, m.id);
        let cellText = "";
        if (mData) {
          if (mData.status === "planned") cellText = "P";
          else if (mData.status === "not_done") cellText = "X";
          else if (mData.status === "done") {
            cellText = "G";
            if (mData.doneDate) {
              const d = new Date(mData.doneDate);
              const formattedDate = `${String(d.getDate()).padStart(
                2,
                "0"
              )}.${String(d.getMonth() + 1).padStart(2, "0")}`;
              cellText += `\n${formattedDate}`;
            }
          }
        }
        rowData.push(cellText);
      });

      tableRows.push(rowData);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 80,
      theme: "grid",
      styles: {
        fontSize: 6,
        cellPadding: 2,
        halign: "center",
        valign: "middle",
        lineColor: [0, 0, 0],
        lineWidth: 0.5,
        textColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [220, 220, 220],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 6,
      },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 50 },
        2: { halign: "left", cellWidth: 120 },
        3: { cellWidth: 35 },
        4: { cellWidth: 60 },
      },
      margin: { bottom: 60 },
      didDrawPage: function (data) {
        const startX = data.settings.margin.left;
        const startY = 25;
        const headerHeight = 45;
        const tableWidth = pageWidth - startX * 2;

        doc.setDrawColor(0);
        doc.setLineWidth(1);
        doc.rect(startX, startY, tableWidth, headerHeight);

        doc.line(startX + 120, startY, startX + 120, startY + headerHeight);
        doc.line(
          pageWidth - startX - 160,
          startY,
          pageWidth - startX - 160,
          startY + headerHeight
        );

        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("FIRMA LOGO", startX + 60, startY + 26, { align: "center" });

        doc.setFontSize(11);
        doc.text(
          `URETIM SAHASI KRITIK EKIPMAN AYLIK-YILLIK BAKIM ve TEMIZLIGI`,
          pageWidth / 2,
          startY + 20,
          { align: "center" }
        );
        doc.setFontSize(9);
        doc.text(
          `${selectedYear} AYLIK BAZDA PLANLANAN (P) GERCEKLESEN ve GERCEKLESMEYEN (G)`,
          pageWidth / 2,
          startY + 35,
          { align: "center" }
        );

        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        const rightColX = pageWidth - startX - 155;
        const rightValX = pageWidth - startX - 70;

        const rowH = headerHeight / 4;
        doc.line(
          pageWidth - startX - 160,
          startY + rowH,
          pageWidth - startX,
          startY + rowH
        );
        doc.line(
          pageWidth - startX - 160,
          startY + rowH * 2,
          pageWidth - startX,
          startY + rowH * 2
        );
        doc.line(
          pageWidth - startX - 160,
          startY + rowH * 3,
          pageWidth - startX,
          startY + rowH * 3
        );

        const textOffsetY = 8;
        doc.text("Dokuman No", rightColX, startY + textOffsetY);
        doc.text(": FR-001", rightValX, startY + textOffsetY);

        doc.text("Yayin Tarihi", rightColX, startY + rowH + textOffsetY);
        doc.text(
          `: ${new Date().toLocaleDateString("tr-TR")}`,
          rightValX,
          startY + rowH + textOffsetY
        );

        doc.text("Revizyon No", rightColX, startY + rowH * 2 + textOffsetY);
        doc.text(": 00", rightValX, startY + rowH * 2 + textOffsetY);

        doc.text("Sayfa No", rightColX, startY + rowH * 3 + textOffsetY);
        doc.text(
          `: ${data.pageNumber}`,
          rightValX,
          startY + rowH * 3 + textOffsetY
        );
      },
    });

    let finalY = doc.lastAutoTable.finalY || 80;

    if (finalY + 60 > pageHeight) {
      doc.addPage();
      finalY = 80;
    }

    const signatureY = finalY + 35;
    const colWidth = pageWidth / 3;

    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);

    doc.setFont("helvetica", "bold");
    doc.text("HAZIRLAYAN", colWidth / 2, signatureY, { align: "center" });
    doc.text("KONTROL EDEN", colWidth + colWidth / 2, signatureY, {
      align: "center",
    });
    doc.text("ONAYLAYAN", colWidth * 2 + colWidth / 2, signatureY, {
      align: "center",
    });

    doc.setFont("helvetica", "normal");
    doc.text("KALITE MUHENDISI", colWidth / 2, signatureY + 15, {
      align: "center",
    });
    doc.text("URETIM MUDURU", colWidth + colWidth / 2, signatureY + 15, {
      align: "center",
    });
    doc.text("GN. MDR. YRD.", colWidth * 2 + colWidth / 2, signatureY + 15, {
      align: "center",
    });

    doc.save(`Ekipman_Planlari_${selectedYear}.pdf`);
  };

  const renderMonthBox = (plan, monthId) => {
    const mData = getMonthData(plan, monthId);
    let boxClass = "month-box-empty";
    let text = "";
    let dateText = "";

    if (mData) {
      if (mData.status === "planned") {
        boxClass = "month-box-planned";
        text = "P";
      } else if (mData.status === "done") {
        boxClass = "month-box-done";
        text = "G";
        if (mData.doneDate) {
          const d = new Date(mData.doneDate);
          dateText = `${String(d.getDate()).padStart(2, "0")}.${String(
            d.getMonth() + 1
          ).padStart(2, "0")}`;
        }
      } else if (mData.status === "not_done") {
        boxClass = "month-box-not-done";
        text = "X";
      }
    }

    return (
      <div
        onClick={() => handleMonthCellClick(plan, monthId)}
        className={`table-month-indicator flex-column ${boxClass} ${
          !canUpdate ? "cursor-not-allowed" : ""
        }`}
        title={canUpdate ? "Detay için tıklayın" : "Yetkiniz yok"}
      >
        <span style={{ lineHeight: dateText ? "1" : "inherit" }}>{text}</span>
        {dateText && (
          <span
            style={{
              fontSize: "9px",
              marginTop: "2px",
              fontWeight: "normal",
              opacity: "0.9",
            }}
          >
            {dateText}
          </span>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div
        className="p-4"
        style={{ backgroundColor: "#f4f7f6", minHeight: "100vh" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="fw-bold text-secondary mb-1">
              ÜRETİM SAHASI KRİTİK EKİPMAN AYLIK-YILLIK BAKIM ve TEMİZLİĞİ
            </h4>
            <p className="text-muted fw-semibold small mb-0">
              {selectedYear} AYLIK BAZDA PLANLANAN (P) GERÇEKLEŞEN ve
              GERÇEKLEŞMEYEN (G)
            </p>
          </div>
          <div className="d-flex gap-3 align-items-center">
            <div className="d-flex align-items-center gap-2 bg-white px-3 py-1 border rounded shadow-sm">
              <Calendar size={16} className="text-muted" />
              <Form.Select
                size="sm"
                className="border-0 fw-bold text-dark shadow-none"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{ cursor: "pointer", minWidth: "90px" }}
              >
                {[...Array(5)].map((_, i) => {
                  const yr = new Date().getFullYear() - 2 + i;
                  return (
                    <option key={yr} value={yr}>
                      {yr}
                    </option>
                  );
                })}
              </Form.Select>
            </div>

            <Button
              variant="outline-secondary"
              className="d-flex align-items-center gap-2 shadow-sm bg-white"
              onClick={handleExportPDF}
            >
              <Download size={18} /> PDF İndir
            </Button>

            {canInsert && (
              <Button
                variant="primary"
                className="d-flex align-items-center gap-2 shadow-sm"
                onClick={() => handleOpenPlanModal()}
              >
                <Plus size={18} /> Yeni Plan
              </Button>
            )}
          </div>
        </div>

        <Card className="border-0 shadow-sm border-radius-15">
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0 custom-table align-middle">
              <thead>
                <tr>
                  <th className="text-center" style={{ width: "3%" }}>
                    NO
                  </th>
                  <th className="ps-4" style={{ width: "10%" }}>
                    Departman
                  </th>
                  <th className="ps-2" style={{ width: "20%" }}>
                    Ekipmanlar
                  </th>
                  <th className="text-center" style={{ width: "6%" }}>
                    Tekrar Sayısı
                  </th>
                  <th style={{ width: "13%" }}>Sorumlu</th>
                  {monthsList.map((m) => (
                    <th
                      key={m.id}
                      className="text-center"
                      style={{ width: "3%" }}
                    >
                      {m.name}
                    </th>
                  ))}
                  {(canUpdate || canDelete) && (
                    <th className="text-end pe-4" style={{ width: "8%" }}>
                      YÖNET
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {plans.length === 0 ? (
                  <tr>
                    <td
                      colSpan={canUpdate || canDelete ? "18" : "17"}
                      className="text-center py-4 text-muted"
                    >
                      Seçili yıla ait plan bulunamadı.
                    </td>
                  </tr>
                ) : (
                  plans.map((plan, index) => (
                    <tr key={plan._id}>
                      <td className="text-center text-muted fw-bold">
                        {plan.siraNo ? plan.siraNo : index + 1}
                      </td>
                      <td className="ps-4 fw-medium text-secondary">
                        {plan.department || "-"}
                      </td>
                      <td className="ps-2 py-3">
                        <div className="fw-bold text-dark">
                          {plan.machineName}
                        </div>
                        <div className="text-muted small">{plan.operation}</div>
                      </td>
                      <td className="text-center fw-medium text-secondary">
                        {plan.repeatPerYear ? `${plan.repeatPerYear}/YIL` : "-"}
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {plan.responsibleUsers?.slice(0, 2).map((u, i) => (
                            <Badge
                              key={i}
                              bg="light"
                              text="dark"
                              className="border fw-normal"
                            >
                              {u.name || "Personel"}
                            </Badge>
                          ))}
                          {plan.responsibleUsers?.length > 2 && (
                            <Badge bg="secondary">
                              +{plan.responsibleUsers.length - 2}
                            </Badge>
                          )}
                        </div>
                      </td>
                      {monthsList.map((m) => (
                        <td key={m.id} className="text-center p-1">
                          {renderMonthBox(plan, m.id)}
                        </td>
                      ))}
                      {(canUpdate || canDelete) && (
                        <td className="text-end pe-4">
                          <div className="d-flex justify-content-end gap-2">
                            {canUpdate && (
                              <Button
                                variant="outline-info"
                                size="sm"
                                className="btn-icon"
                                onClick={() => handleOpenPlanModal(plan)}
                              >
                                <Edit2 size={14} />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="btn-icon"
                                onClick={() => handleDelete(plan._id)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        <Modal
          show={showPlanModal}
          onHide={() => setShowPlanModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="fw-bold">
              {editingPlan ? "Planı Güncelle" : "Yeni Ekipman Planı"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Form>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small">
                      Sıra No
                    </Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="Örn: 1"
                      value={formData.siraNo}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          siraNo: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small">
                      Departman
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Örn: Üretim"
                      value={formData.department}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          department: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={5}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small">
                      Makina / Ekipman Adı
                    </Form.Label>
                    <Form.Control
                      value={formData.machineName}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          machineName: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small">Yıl</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.year}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          year: parseInt(e.target.value),
                        })
                      }
                      disabled={!!editingPlan}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small">
                      Tekrar Sayısı (/Yıl)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      placeholder="Örn: 3"
                      value={formData.repeatPerYear}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          repeatPerYear: e.target.value
                            ? parseInt(e.target.value)
                            : "",
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small">
                      Sorumlu Personeller
                    </Form.Label>
                    <Select
                      isMulti
                      options={personels}
                      value={formData.responsibleUsers}
                      onChange={(selected) =>
                        setFormData({ ...formData, responsibleUsers: selected })
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold small">
                      İşlem Detayı
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.operation}
                      onChange={(e) =>
                        setFormData({ ...formData, operation: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Label className="fw-semibold small mb-2">
                    Planlanan Aylar (Varsayılan olarak 'Planlandı' atanır)
                  </Form.Label>
                  <div className="month-grid">
                    {monthsList.map((m) => {
                      const isActive = formData.months.some(
                        (x) => x.month === m.id
                      );
                      return (
                        <div
                          key={m.id}
                          className={`month-box ${isActive ? "selected" : ""}`}
                          onClick={() => {
                            let updated;
                            if (isActive) {
                              updated = formData.months.filter(
                                (x) => x.month !== m.id
                              );
                            } else {
                              updated = [
                                ...formData.months,
                                { month: m.id, status: "planned" },
                              ];
                            }
                            setFormData({ ...formData, months: updated });
                          }}
                        >
                          {m.name}
                        </div>
                      );
                    })}
                  </div>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer className="border-0 bg-light p-3">
            <Button
              variant="link"
              className="text-muted text-decoration-none"
              onClick={() => setShowPlanModal(false)}
            >
              İptal
            </Button>
            <Button variant="primary" className="px-4" onClick={handleSavePlan}>
              Kaydet
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showStatusModal}
          onHide={() => setShowStatusModal(false)}
          centered
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="fw-bold fs-5">
              İşlem Durumu Güncelle
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold small">Durum</Form.Label>
              <Form.Select
                value={statusForm.status}
                onChange={(e) =>
                  setStatusForm({ ...statusForm, status: e.target.value })
                }
              >
                <option value="planned">Planlandı (Bekliyor)</option>
                <option value="done">Gerçekleşti (Tamamlandı)</option>
                <option value="not_done">
                  Gerçekleşmedi (İptal / Gecikti)
                </option>
              </Form.Select>
            </Form.Group>

            {statusForm.status === "done" && (
              <Form.Group className="mb-3">
                {" "}
                {/* mb-4'ü mb-3 yaptım ki açıklama ile arası açık olmasın */}
                <Form.Label className="fw-semibold small">
                  Gerçekleşme Tarihi
                </Form.Label>
                <Form.Control
                  type="date"
                  value={statusForm.doneDate}
                  onChange={(e) =>
                    setStatusForm({ ...statusForm, doneDate: e.target.value })
                  }
                />
              </Form.Group>
            )}

            {/* Yeni Eklenen Açıklama Alanı */}
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold small">
                Açıklama / Not
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Bu aya ait özel bir not veya açıklama girebilirsiniz..."
                value={statusForm.description}
                onChange={(e) =>
                  setStatusForm({ ...statusForm, description: e.target.value })
                }
              />
            </Form.Group>

            <div className="d-flex justify-content-between align-items-center">
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleRemoveMonth}
              >
                Plandan Çıkar
              </Button>
              <Button
                variant="success"
                className="px-4"
                onClick={handleSaveStatus}
              >
                Durumu Kaydet
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </div>

      <style>{`
        .custom-table thead th { background-color: #f8f9fa; text-transform: uppercase; font-size: 11px; letter-spacing: 0.5px; color: #6c757d; border-bottom: 1px solid #eee; vertical-align: middle; }
        .table-month-indicator { width: 100%; min-height: 40px; padding: 4px 0; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold; cursor: pointer; transition: all 0.2s; border: 1px solid transparent; overflow: hidden; }
        
        .month-box-empty { background: #f8f9fa; border-color: #e9ecef; color: transparent; }
        .month-box-empty:hover { background: #e2e6ea; }
        
        .month-box-planned { background: #fff3cd; color: #856404; border-color: #ffeeba; }
        .month-box-planned:hover { background: #ffe8a1; }
        
        .month-box-done { background: #d1e7dd; color: #0f5132; border-color: #badbcc; }
        .month-box-done:hover { background: #bfe2d1; }
        
        .month-box-not-done { background: #f8d7da; color: #842029; border-color: #f5c2c7; }
        .month-box-not-done:hover { background: #f1b0b7; }

        .cursor-not-allowed { cursor: not-allowed !important; }

        .legend-box { display: inline-block; width: 14px; height: 14px; border-radius: 3px; border: 1px solid rgba(0,0,0,0.1); }
        .btn-icon { width: 32px; height: 32px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 6px; }
        .month-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; }
        .month-box { padding: 8px; border: 1px solid #dee2e6; border-radius: 6px; text-align: center; cursor: pointer; font-size: 13px; font-weight: 500; transition: 0.2s; background: #fff; color: #495057; }
        .month-box:hover { border-color: #0d6efd; background: #f8f9ff; }
        .month-box.selected { background: #0d6efd; color: white; border-color: #0d6efd; }
        .border-radius-15 { border-radius: 12px; overflow: hidden; }
      `}</style>
    </Layout>
  );
}

export default EquipmentPlanManagement;
